import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Tray, PaperPlaneTilt, FileText, Archive, Trash, PencilSimpleLine,
  Plus, AddressBook, Lightning, CalendarBlank, Gear, ShieldStar,
  SignOut, CaretLeft, WarningOctagon, UsersThree, Brain, CreditCard,
  Folder, PencilSimple, Check, X
} from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";
import { useAuth } from "@/App";
import { TokenWidget } from "@/components/TokenWidget";
import { t } from "@/lib/i18n";

const FOLDER_ITEMS = [
  { key: "inbox", tkey: "nav.inbox", label: "Inbox", icon: Tray },
  { key: "sent", tkey: "nav.sent", label: "Sent", icon: PaperPlaneTilt },
  { key: "drafts", tkey: "nav.drafts", label: "Drafts", icon: FileText },
  { key: "archive", tkey: "nav.archive", label: "Archive", icon: Archive },
  { key: "trash", tkey: "nav.trash", label: "Trash", icon: Trash },
];

export const Sidebar = ({ counts = {}, activeFolder, activeMailbox = "", onSelectFolder, onCompose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onInbox = location.pathname === "/app";
  const [folders, setFolders] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [shared, setShared] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [siCounts, setSiCounts] = useState({});
  const [priorityOn, setPriorityOn] = useState(false);

  useEffect(() => {
    api.get("/priority/enabled").then((r) => setPriorityOn(!!r.data.enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/shared-inboxes").then((r) => {
      const items = r.data.shared_inboxes || [];
      setShared(items);
      items.forEach((s) => {
        if (s.active) api.get("/emails/counts", { params: { mailbox: s.id } })
          .then((c) => setSiCounts((prev) => ({ ...prev, [s.id]: c.data }))).catch(() => {});
      });
    }).catch(() => {});
  }, []);

  const loadFolders = () => api.get("/folders").then((r) => setFolders(r.data)).catch(() => {});
  useEffect(() => { loadFolders(); }, []);

  const createFolder = async () => {
    const name = newName.trim();
    if (!name) { setAdding(false); return; }
    try {
      await api.post("/folders", { name });
      toast.success(`Folder "${name}" created`);
      setNewName(""); setAdding(false); loadFolders();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const renameFolder = async (f) => {
    const name = window.prompt("Rename folder", f.name);
    if (!name || name.trim() === f.name) return;
    try {
      await api.patch(`/folders/${f.id}`, { name: name.trim() });
      toast.success("Folder renamed");
      loadFolders();
      if (activeFolder === f.id) onSelectFolder?.(f.id, name.trim());
    } catch (e) { toast.error(errDetail(e)); }
  };
  const deleteFolder = async (f) => {
    if (!window.confirm(`Delete folder "${f.name}"? Its emails move to Archive.`)) return;
    try {
      await api.delete(`/folders/${f.id}`);
      toast.success("Folder deleted");
      loadFolders();
      if (activeFolder === f.id) onSelectFolder?.("inbox");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const NavRow = ({ icon: Icon, label, active, onClick, badge, testId }) => (
    <button data-testid={testId} onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${active ? "bg-blue-50 text-blue-700 font-semibold" : "text-gray-700 hover:bg-gray-100"}`}>
      <Icon size={17} weight={active ? "fill" : "regular"} />
      <span className="flex-1 text-left">{label}</span>
      {badge != null && badge > 0 && <span className="text-xs bg-gray-200 text-gray-700 rounded-full px-2 py-0.5">{badge}</span>}
    </button>
  );

  const initials = (user?.name || user?.email || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <aside className="w-64 shrink-0 h-full bg-gray-50 border-r border-gray-200 flex flex-col">
      <div className="px-4 pt-4 pb-2 shrink-0">
        <TokenWidget />
        <button data-testid="compose-button" onClick={() => (onCompose ? onCompose() : navigate("/compose"))}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 transition-colors text-white rounded-md py-2.5 text-sm font-semibold">
          <PencilSimpleLine size={16} weight="bold" /> {t("nav.compose", "Compose")}
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto scroll-thin pb-3">
        <nav className="px-3 space-y-0.5 mt-1">
          {priorityOn && (
            <NavRow icon={Lightning} label={`⚡ ${t("pi.nav", "Priority")}`} testId="nav-priority"
              active={location.pathname === "/priority"}
              onClick={() => navigate("/priority")} />
          )}
          {FOLDER_ITEMS.map((f) => (
            <NavRow key={f.key} icon={f.icon} label={t(f.tkey, f.label)} testId={`folder-${f.key}`}
              active={onInbox && activeFolder === f.key}
              badge={f.key === "inbox" ? counts.unread : counts[f.key] && f.key === "drafts" ? counts.drafts : null}
              onClick={() => { if (!onInbox) navigate("/app"); onSelectFolder?.(f.key); }} />
          ))}
        </nav>

        <div className="px-3 mt-5">
          <div className="flex items-center justify-between px-3 mb-1">
            <span className="text-[11px] font-semibold tracking-wider text-gray-400">{t("nav.folders", "FOLDERS")}</span>
            <button data-testid="add-folder-button" onClick={() => { setAdding(!adding); setNewName(""); }}
              className="text-gray-400 hover:text-gray-700 transition-colors"><Plus size={14} /></button>
          </div>
          {adding && (
            <div className="flex items-center gap-1 px-3 mb-1">
              <input data-testid="new-folder-input" autoFocus value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") createFolder(); if (e.key === "Escape") setAdding(false); }}
                placeholder={t("nav.folder_name_ph", "Folder name")} className="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1 text-xs" />
              <button data-testid="new-folder-save" onClick={createFolder} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check size={14} /></button>
              <button data-testid="new-folder-cancel" onClick={() => setAdding(false)} className="p-1 text-gray-400 hover:bg-gray-100 rounded"><X size={14} /></button>
            </div>
          )}
          <NavRow icon={WarningOctagon} label={t("nav.spam", "Spam")} testId="folder-spam"
            active={onInbox && activeFolder === "spam"} badge={counts.spam}
            onClick={() => { if (!onInbox) navigate("/app"); onSelectFolder?.("spam"); }} />
          {folders.map((f) => (
            <div key={f.id} className="group relative">
              <NavRow icon={Folder} label={f.name} testId={`custom-folder-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                active={onInbox && activeFolder === f.id} badge={counts[f.id]}
                onClick={() => { if (!onInbox) navigate("/app"); onSelectFolder?.(f.id, f.name); }} />
              <div className="absolute right-1.5 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-0.5 bg-inherit">
                <button data-testid={`rename-folder-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} title={t("sidebar.rename", "Rename")}
                  onClick={(e) => { e.stopPropagation(); renameFolder(f); }}
                  className="p-1 text-gray-400 hover:text-gray-700 bg-gray-50 rounded"><PencilSimple size={12} /></button>
                <button data-testid={`delete-folder-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} title={t("common.delete", "Delete")}
                  onClick={(e) => { e.stopPropagation(); deleteFolder(f); }}
                  className="p-1 text-gray-400 hover:text-red-600 bg-gray-50 rounded"><Trash size={12} /></button>
              </div>
            </div>
          ))}
        </div>

        {shared.length > 0 && (
          <div className="px-3 mt-5" data-testid="sidebar-shared-section">
            <div className="px-3 mb-1 text-[11px] font-semibold tracking-wider text-gray-400">{t("nav.shared_inboxes", "SHARED INBOXES")}</div>
            {shared.map((s) => (
              <div key={s.id}>
                <button data-testid={`si-nav-${s.id}`}
                  onClick={() => {
                    const entering = !expanded[s.id] || activeMailbox !== s.id;
                    setExpanded((e) => ({ ...e, [s.id]: activeMailbox === s.id ? !e[s.id] : true }));
                    if (entering && s.active) { if (!onInbox) navigate(`/app?mailbox=${s.id}`); onSelectFolder?.("inbox", "", s.id, s.name); }
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${activeMailbox === s.id ? "bg-amber-50 text-amber-800 font-semibold" : "text-gray-700 hover:bg-gray-100"}`}>
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.color || "#2563eb" }} />
                  <span className="flex-1 text-left truncate">{s.name}</span>
                  {!s.active && <span className="text-[9px] bg-amber-100 text-amber-700 rounded-full px-1.5">{t("si.frozen", "inactive")}</span>}
                  {s.active && siCounts[s.id]?.unread > 0 && <span data-testid={`si-nav-unread-${s.id}`} className="text-xs bg-amber-200 text-amber-800 rounded-full px-2 py-0.5">{siCounts[s.id].unread}</span>}
                </button>
                {expanded[s.id] && s.active && (
                  <div className="ml-5 space-y-0.5" data-testid={`si-nav-folders-${s.id}`}>
                    {FOLDER_ITEMS.map((f) => (
                      <button key={f.key} data-testid={`si-folder-${s.id}-${f.key}`}
                        onClick={() => { if (!onInbox) navigate(`/app?mailbox=${s.id}`); onSelectFolder?.(f.key, "", s.id, s.name); }}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-xs transition-colors ${activeMailbox === s.id && activeFolder === f.key ? "bg-amber-50 text-amber-800 font-semibold" : "text-gray-600 hover:bg-gray-100"}`}>
                        <f.icon size={14} /> <span className="flex-1 text-left">{t(f.tkey, f.label)}</span>
                        {f.key === "inbox" && siCounts[s.id]?.unread > 0 && <span className="text-[10px] text-amber-700">{siCounts[s.id].unread}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="px-3 mt-5">
          <div className="px-3 mb-1 text-[11px] font-semibold tracking-wider text-gray-400">{t("nav.workspace", "WORKSPACE")}</div>
          <NavRow icon={AddressBook} label={t("nav.contacts", "Address Book")} testId="nav-contacts" active={location.pathname === "/contacts"} onClick={() => navigate("/contacts")} />
          <NavRow icon={Lightning} label={t("nav.rules", "Rules")} testId="nav-rules" active={location.pathname === "/rules"} onClick={() => navigate("/rules")} />
          <NavRow icon={Brain} label={t("nav.memory", "Memory")} testId="nav-memory" active={location.pathname === "/memory"} onClick={() => navigate("/memory")} />
          <NavRow icon={CalendarBlank} label={t("nav.calendar", "Calendar")} testId="nav-calendar" active={location.pathname === "/calendar"} onClick={() => navigate("/calendar")} />
          <NavRow icon={UsersThree} label={t("nav.team", "Team")} testId="nav-team" active={location.pathname === "/team"} onClick={() => navigate("/team")} />
          <NavRow icon={Gear} label={t("nav.settings", "Settings")} testId="nav-settings" active={location.pathname === "/settings"} onClick={() => navigate("/settings")} />
          <NavRow icon={CreditCard} label={t("nav.billing", "Billing")} testId="nav-billing" active={location.pathname === "/billing"} onClick={() => navigate("/billing")} />
          {user?.role === "superadmin" && (
            <NavRow icon={ShieldStar} label={t("nav.admin", "Admin Panel")} testId="nav-admin" active={location.pathname.startsWith("/admin")} onClick={() => navigate("/admin/keys")} />
          )}
        </div>
      </div>

      <div className="p-3 border-t border-gray-200 shrink-0 flex items-center gap-2.5">
        <div className="w-8 h-8 shrink-0 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{initials}</div>
        <div className="flex-1 min-w-0">
          <div data-testid="sidebar-user-name" className="font-heading font-bold text-gray-900 text-[13px] leading-tight truncate">{user?.name}</div>
          <div data-testid="sidebar-user-email" className="text-[11px] text-gray-500 truncate">{user?.email}</div>
        </div>
        <button data-testid="logout-button" onClick={logout} title={t("nav.sign_out", "Sign out")}
          className="shrink-0 p-2 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors">
          <SignOut size={17} />
        </button>
      </div>
    </aside>
  );
};
